/**
 * Client Request ▸ **Route Tracking** (design v2.0 §12; prototype v46).
 *
 * Three views of the same work, in the order dispatch does it:
 *
 *   PENDING — scheduled services not on a route yet. Tick a day's worth, name a
 *             driver, create the route. This is the screen's entry point
 *             because an unrouted service is the thing that needs a decision.
 *   ROUTES  — the dated runs, with progress and status.
 *   STOPS   — every stop across every route, for looking one up.
 *
 * A ROUTE IS A DATED RUN — a driver, a vehicle and ordered stops on a given
 * day: RT260904-01. A STOP is not a separate record either: it IS a
 * Service Task, pointing at the route and holding a position, so there are
 * never two rows describing one visit that can disagree about where it is.
 *
 * STATUS IS DERIVED, never stored — v46: "a route is complete when its last
 * stop is". `routeStatus` is the one rule, read by the list and the detail.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import '../backlog/backlog.css';
// The detail view's form controls are the shared sd-label / sd-input, so it
// declares that dependency itself. It had been relying on another page having
// imported the file first — true in practice, but one route change away from
// rendering unstyled.
import '../modules/SettingsDetailPage.css';
import './clientRequest.css';
import RouteMap from './RouteMap';
import { AuditTimeline } from '../audit/AuditTimeline';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { useNotify } from '../../components/notifications';
import { useConfirm, showDialog } from '../../components/confirm';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { fmtLocalDate, fmtLocalDateTime } from '../../utils/datetime';
import {
  AUDIT_FORM_NAME, createRoute, deleteRoute, fetchRoutePending, fetchRouteStops,
  fetchRouteRuns, fetchServiceDefinitions, fetchTechnicians, moveRouteStop,
  pendingReadiness, routePublishBlockers, routeStatus, updateRoute, fetchRouteVehicles,
  type RoutePendingRow, type RouteRow, type RouteStopRow, type ServiceCategoryDef,
  fetchAuditTrail, type AuditTrailRow,
} from './clientRequestApi';

type Tab = 'pending' | 'routes' | 'stops';

export default function RouteTrackingPage() {
  const [tab,     setTab]     = useState<Tab>('pending');
  const [pending, setPending] = useState<RoutePendingRow[]>([]);
  const [routes,  setRoutes]  = useState<RouteRow[]>([]);
  const [stops,   setStops]   = useState<RouteStopRow[]>([]);
  const [defs,    setDefs]    = useState<ServiceCategoryDef[]>([]);
  const [techs,   setTechs]   = useState<{ id: string; label: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadMs,  setLoadMs]  = useState<number>();
  const [busy,    setBusy]    = useState(false);

  const [sel,        setSel]        = useState<Set<string | number>>(new Set());
  const [bulkDriver, setBulkDriver] = useState('');
  const [openId,     setOpenId]     = useState<string | null>(null);

  const notify = useNotify();
  const { confirm } = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const [p, r, s, d, t] = await Promise.all([
        fetchRoutePending(), fetchRouteRuns(), fetchRouteStops(),
        fetchServiceDefinitions(), fetchTechnicians(),
      ]);
      setPending(p); setRoutes(r); setStops(s); setDefs(d); setTechs(t);
      setLoadMs(Math.round(performance.now() - t0));
    } catch (e) {
      notify.error(e instanceof Error ? `Could not load route tracking: ${e.message}`
                                      : 'Could not load route tracking.');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  const catName = useCallback(
    (code?: string | null) => defs.find(d => d.code === code)?.name ?? code ?? '—', [defs]);
  const catTone = useCallback(
    (code?: string | null) => defs.find(d => d.code === code)?.tone ?? 'neutral', [defs]);

  /* ── Create a route from the ticked services ─────────────────────────────
     A route is ONE day's work, so a selection spanning two days is refused
     rather than quietly split or silently driven: handing a driver a round
     they cannot drive is worse than making them tick again. */
  const createFromSelection = async () => {
    const picked = pending.filter(p => sel.has(p.id));
    // Nothing ticked is a mistake, not a no-op: an inert button leaves the user
    // clicking it again wondering what is broken.
    if (!picked.length) {
      notify.warning('Tick the services that belong on this route first — a route is '
                   + "one driver on one day, carrying a set of stops.");
      return;
    }

    const days = [...new Set(picked.map(p => (p.scheduledDate ?? '').slice(0, 10)))];
    if (days.some(d => !d)) {
      notify.warning('A route runs on a day, so every service on it needs a scheduled '
                   + 'date. Set one in Service Allocation first.');
      return;
    }
    if (days.length > 1) {
      notify.warning(
        `A route is one day's work, and these span ${days.length} days `
        + `(${days.sort().join(', ')}). Tick the services for a single day.`);
      return;
    }

    const driver = techs.find(t => t.id === bulkDriver);
    // A route IS a driver's day — without one the round belongs to nobody, and
    // it could not be published either.
    if (!driver) {
      notify.warning('Choose who is driving from the control above the list. A route '
                   + "cannot be built without a driver, because the route IS a driver's day.");
      return;
    }

    const ok = await confirm({
      title: 'Create route',
      message: `${picked.length} stop${picked.length > 1 ? 's' : ''} on ${fmtLocalDate(days[0])}, `
             + `driven by ${driver.label}, in the order shown. `
             + `${driver.label} is assigned to `
             + `${picked.length === 1 ? 'the service' : 'each of these services'} by this. `
             + 'Every change is audited. Do you want to continue?',
    });
    if (!ok) return;

    setBusy(true);
    try {
      const made = await createRoute({
        routeName: null, routeDate: days[0] ?? null,
        driverId: driver.id, vehicleCode: null,
        serviceIds: picked.map(p => p.id),
      });
      notify.success(`Route ${made.routeNo} created with ${made.stopsAttached} stop`
                     + `${made.stopsAttached > 1 ? 's' : ''}.`);
      setSel(new Set()); setBulkDriver('');
      await load();
      setTab('routes');
    } catch (e) {
      notify.error(e instanceof Error ? `Could not create the route: ${e.message}`
                                      : 'Could not create the route.');
    } finally {
      setBusy(false);
    }
  };

  // ── Columns ─────────────────────────────────────────────────────────────
  const pendingCols: AppColumn<RoutePendingRow>[] = useMemo(() => [
    { field: 'scheduledDate', header: 'When', width: 170,
      text: r => whenText(r.scheduledDate, r.scheduledTime),
      render: r => whenText(r.scheduledDate, r.scheduledTime) || <span className="na">—</span> },
    { field: 'serviceNo', header: 'Service', width: 150,
      text: r => r.serviceNo ?? '',
      render: r => r.serviceNo ?? <span className="na">—</span> },
    { field: 'categoryCode', header: 'Category', width: 130,
      text: r => catName(r.categoryCode),
      render: r => <span className={`cr-chip ${catTone(r.categoryCode)}`}>{catName(r.categoryCode)}</span> },
    { field: 'clientName', header: 'Client', width: 180,
      text: r => r.clientName ?? '',
      render: r => r.clientName ?? <span className="na">—</span> },
    { field: 'siteAddress', header: 'Where', width: 190,
      text: r => r.siteAddress ?? '',
      render: r => r.siteAddress || <span className="na">—</span> },
    { field: 'assignedToName', header: 'Driver', width: 160,
      text: r => r.assignedToName ?? 'Unassigned',
      render: r => r.assignedToName ?? <span className="na">Unassigned</span> },
    { field: 'readiness', header: 'Readiness', width: 150, sortable: false,
      text: r => pendingReadiness(r).label,
      render: r => {
        const s = pendingReadiness(r);
        return <span className={`cr-chip ${s.tone}`}>{s.label}</span>;
      } },
  ], [catName, catTone]);

  /* The route on a map (prototype v68 showRouteMap): the route's state in one
     line, then the live map beside its stops in visit order. Opened from the
     Routes list's Map column without leaving the list. It is the SHARED dialog
     in its `wide` variant — a second, bespoke modal is what standing rule 7
     exists to prevent. Read-only, so its only action is Close. */
  const showRouteMap = useCallback((r: RouteRow) => {
    const own = stops
      .filter(s => s.routeId === r.id)
      .sort((a, b) => (a.routeSeq ?? 0) - (b.routeSeq ?? 0));
    const st = routeStatus(r);
    void showDialog({
      type: 'info',
      variant: 'wide',
      title: `${r.routeNo ?? 'Route'}${r.routeName ? ` · ${r.routeName}` : ''}`,
      message: (
        <div className="rtm">
          <div className="rtm-head">
            <span className={`cr-chip ${st.tone}`}>{st.label}</span>
            <span className="rtm-meta">
              {[r.routeDate ? fmtLocalDate(r.routeDate) : 'No date',
                r.driverName ?? 'unassigned',
                r.vehicleCode ?? 'no vehicle'].join(' · ')}
            </span>
            <span className="rtm-prog">
              {r.completedCount} of {r.stopCount} stop{r.stopCount === 1 ? '' : 's'} complete
            </span>
          </div>
          <div className="rt-maprow">
            <RouteMap stops={own} />
            <RouteStations stops={own} catName={catName} catTone={catTone} />
          </div>
        </div>
      ),
      actions: [{ label: 'Close', primary: true }],
    });
  }, [stops, catName, catTone]);

  const routeCols: AppColumn<RouteRow>[] = useMemo(() => [
    { field: 'routeNo', header: 'Route', width: 150,
      text: r => r.routeNo ?? '',
      render: r => (
        <button type="button" className="cr-linkcell"
                onClick={e => { e.stopPropagation(); setOpenId(r.id); }}>
          {r.routeNo ?? '—'}
        </button>
      ) },
    { field: 'routeName', header: 'Name', width: 200,
      text: r => r.routeName ?? '',
      render: r => r.routeName || <span className="na">—</span> },
    { field: 'routeDate', header: 'Date', width: 140, type: 'date',
      text: r => r.routeDate ? fmtLocalDate(r.routeDate) : '',
      render: r => r.routeDate ? fmtLocalDate(r.routeDate) : <span className="na">—</span> },
    { field: 'driverName', header: 'Driver', width: 160,
      text: r => r.driverName ?? '',
      render: r => r.driverName ?? <span className="na">—</span> },
    { field: 'vehicleCode', header: 'Vehicle', width: 130,
      text: r => r.vehicleCode ?? '',
      render: r => r.vehicleCode ?? <span className="na">—</span> },
    { field: 'stopCount', header: 'Stops', width: 100,
      text: r => `${r.completedCount} / ${r.stopCount}`,
      render: r => `${r.completedCount} / ${r.stopCount}` },
    { field: 'progress', header: 'Progress', width: 150, sortable: false,
      text: r => `${pct(r)}%`,
      render: r => (
        <span className="rt-bar" title={`${pct(r)}% complete`}>
          <span className="rt-bar-fill" style={{ width: `${pct(r)}%` }} />
        </span>
      ) },
    { field: 'status', header: 'Status', width: 130, sortable: false,
      text: r => routeStatus(r).label,
      render: r => {
        const s = routeStatus(r);
        return <span className={`cr-chip ${s.tone}`}>{s.label}</span>;
      } },
    { field: 'blocking', header: 'Blocking publish', width: 180, sortable: false,
      // Named, not counted: "cannot publish" leaves the dispatcher hunting.
      text: r => r.publishedAt ? '' : routePublishBlockers(r).join(', '),
      render: r => {
        if (r.publishedAt) return <span className="na">—</span>;
        const b = routePublishBlockers(r);
        return b.length ? <span className="rt-block">{b.join(', ')}</span> : <span className="na">—</span>;
      } },
    { field: 'map', header: 'Map', width: 80, align: 'center',
      sortable: false, filterable: false, noExport: true,
      text: () => '',
      // Its own click: the row opens the route's detail, this opens the map
      // over the list — so it must not bubble into the row.
      render: r => (
        <button type="button" className="rt-mapbtn" title="Show this route on a map"
                aria-label={`Show ${r.routeNo ?? 'the route'} on a map`}
                onClick={e => { e.stopPropagation(); showRouteMap(r); }}>
          <i className="ti ti-map-2" />
        </button>
      ) },
  ], [showRouteMap]);

  /* The Stops list in v68's columns and order: RouteID · RouteName · RouteDate ·
     Driver · Client · JobID · RequestID · ServiceID · Category · Status · Remark · Map.
     The driver is the ROUTE's (who drives the round), read from the route row;
     the map opens that route over the list, as the Routes tab's does. Remark
     is the ROUTE's note, entered in the route's detail. */
  const stopCols: AppColumn<RouteStopRow>[] = useMemo(() => {
    const routeOf = (s: RouteStopRow) => routes.find(r => r.id === s.routeId);
    const na = <span className="na">—</span>;
    return [
      { field: 'routeNo', header: 'RouteID', width: 130,
        text: r => r.routeNo ?? '',
        render: r => (
          <button type="button" className="cr-linkcell"
                  onClick={e => { e.stopPropagation(); setOpenId(r.routeId); }}>
            {r.routeNo ?? '—'}
          </button>
        ) },
      { field: 'routeName', header: 'RouteName', width: 190,
        text: r => r.routeName ?? '',
        render: r => r.routeName || na },
      { field: 'routeDate', header: 'RouteDate', width: 120,
        text: r => (r.routeDate ? fmtLocalDate(r.routeDate) : ''),
        render: r => (r.routeDate ? fmtLocalDate(r.routeDate) : na) },
      { field: 'driver', header: 'Driver', width: 150,
        text: r => routeOf(r)?.driverName ?? '',
        render: r => routeOf(r)?.driverName || na },
      { field: 'clientName', header: 'Client', width: 180,
        text: r => r.clientName ?? '',
        render: r => r.clientName || na },
      { field: 'jobNo', header: 'JobID', width: 120,
        text: r => r.jobNo ?? '',
        render: r => r.jobNo || na },
      { field: 'requestNo', header: 'RequestID', width: 130,
        text: r => r.requestNo ?? '',
        render: r => r.requestNo || na },
      { field: 'serviceNo', header: 'ServiceID', width: 150,
        text: r => r.serviceNo ?? '',
        render: r => r.serviceNo || na },
      { field: 'categoryCode', header: 'Category', width: 130,
        text: r => catName(r.categoryCode),
        render: r => <span className={`cr-chip ${catTone(r.categoryCode)}`}>{catName(r.categoryCode)}</span> },
      { field: 'stage', header: 'Status', width: 130, sortable: false,
        text: r => stopStage(r).label,
        render: r => {
          const s = stopStage(r);
          return <span className={`cr-chip ${s.tone}`}>{s.label}</span>;
        } },
      { field: 'routeRemark', header: 'Remark', width: 200,
        text: r => r.routeRemark ?? '',
        render: r => (r.routeRemark
          ? <span title={r.routeRemark}>{r.routeRemark}</span>
          : na) },
      { field: 'map', header: 'Map', width: 80, align: 'center',
        sortable: false, filterable: false, noExport: true,
        text: () => '',
        render: r => {
          const rt = routeOf(r);
          return rt ? (
            <button type="button" className="rt-mapbtn" title="Show this route on a map"
                    aria-label={`Show ${rt.routeNo ?? 'the route'} on a map`}
                    onClick={e => { e.stopPropagation(); showRouteMap(rt); }}>
              <i className="ti ti-map-2" />
            </button>
          ) : na;
        } },
    ];
  }, [catName, catTone, routes, showRouteMap]);

  // Keyboard shortcut (CLAUDE.md standing rule 14, §18.6, ADR-0013): "Create
  // route" lives in the Pending tab's selection bar (shown only there, and only
  // when rows are ticked), so gate the def to that context — the route detail's
  // Save/Publish keys live in RouteDetail, so nothing collides.  Alt+N = Create route.
  useHotkeys(
    tab === 'pending' && sel.size > 0
      ? [{ combo: 'alt+n', label: 'Create route', group: 'Client Request',
           run: () => void createFromSelection(), disabled: busy },
         { combo: 'alt+shift+c', label: 'Clear', group: 'Client Request',
           run: () => setSel(new Set()) }]
      : [],
  );

  const open = openId ? routes.find(r => r.id === openId) ?? null : null;
  if (open) {
    return (
      <RouteDetail
        route={open}
        stops={stops.filter(s => s.routeId === open.id)}
        techs={techs}
        catName={catName}
        catTone={catTone}
        onBack={() => setOpenId(null)}
        onChanged={load}
      />
    );
  }

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span>
            <i className="ti ti-chevron-right" />
            <span>Client Request</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Route Tracking</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="rttrack" loadMs={loadMs} loading={loading}
                       auditFormName={AUDIT_FORM_NAME} />
          </div>
          <div className="dp-subtitle">
            {tab === 'pending' ? 'Scheduled services waiting to be put on a route.'
              : tab === 'routes' ? 'Status is derived from the stops — a route is complete when its last stop is.'
              : 'Every stop across every route, in visit order.'}
          </div>
        </div>

        <AppToolbar
          left={
            <div className="rt-tabs" role="tablist" aria-label="Route Tracking views">
              {([
                ['pending', `Pending (${pending.length})`],
                ['routes',  'Routes'],
                ['stops',   'Stops'],
              ] as [Tab, string][]).map(([id, label]) => (
                <button key={id} type="button" role="tab" aria-selected={tab === id}
                        className={`rt-tab${tab === id ? ' on' : ''}`}
                        onClick={() => { setTab(id); setSel(new Set()); }}>
                  {label}
                </button>
              ))}
            </div>
          }
          onRefresh={() => void load()}
          refreshing={loading}
          refreshDisabled={loading}
          exportData={
            tab === 'pending' ? { ...gridExportMatrix(pending, pendingCols), filename: 'route-pending' }
            : tab === 'routes' ? { ...gridExportMatrix(routes, routeCols),   filename: 'routes' }
            :                    { ...gridExportMatrix(stops, stopCols),     filename: 'route-stops' }
          }
        />

        {tab === 'pending' && sel.size > 0 && (
          /* Ticked rows show the action bar. The orange hint strip was removed
             app-wide (user request 2026-09-19). */
          <div className="sa-bulk">
            <span className="sa-bulk-n">{sel.size} selected</span>
            <label className="rt-bulk-lbl" htmlFor="rt-driver">Driver</label>
            <select id="rt-driver" className="sa-bulk-f" value={bulkDriver}
                    onChange={e => setBulkDriver(e.target.value)}>
              <option value="">— choose —</option>
              {techs.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
            <button className="ca-btn ca-btn-solid" disabled={busy}
                    title={`Create route (${formatCombo('alt+n')})`}
                    onClick={() => void createFromSelection()}>
              <span className="tb-i"><i className="ti ti-route" /></span>
              Create route
            </button>
            <span className="sa-bulk-gap" />
            <button className="ca-btn" onClick={() => setSel(new Set())}
                    title={`Clear (${formatCombo('alt+shift+c')})`}>Clear</button>
          </div>
        )}
      </div>

      <div className="bl-grid-area" style={{ marginTop: 10 }}>
        <div className="bl-card bl-card-flex">
          {tab === 'pending' && (
            <AppDataGrid
              rows={pending} columns={pendingCols} getRowId={r => r.id}
              defaultPageSize={20} loading={loading}
              selectable selectedIds={sel} onSelectionChange={setSel}
              emptyText="Nothing waiting — every scheduled service is on a route."
            />
          )}
          {tab === 'routes' && (
            <AppDataGrid
              rows={routes} columns={routeCols} getRowId={r => r.id}
              defaultPageSize={20} loading={loading}
              // The same leading checkbox column as Pending — one shared
              // `selectable`, cleared on every tab change like Pending's.
              selectable selectedIds={sel} onSelectionChange={setSel}
              onRowClick={r => setOpenId(r.id)}
              emptyText="No routes yet — tick some pending services and create one."
            />
          )}
          {tab === 'stops' && (
            <AppDataGrid
              rows={stops} columns={stopCols} getRowId={r => r.id}
              defaultPageSize={20} loading={loading}
              selectable selectedIds={sel} onSelectionChange={setSel}
              onRowClick={r => setOpenId(r.routeId)}
              emptyText="No stops yet."
            />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * A route's stops as a visit-order list — the Laboratory it departs from, then
 * each stop numbered as its pin is on the map (prototype v68 routeStations).
 * Beside the map in both the Map dialog and the route's detail, so the picture
 * and the list always read the same way round.
 */
function RouteStations({ stops, catName, catTone }: {
  stops: RouteStopRow[];
  catName: (code?: string | null) => string;
  catTone: (code?: string | null) => string;
}) {
  return (
    <div className="rtm-list">
      <div className="rtm-stn">
        <span className="rtm-pin dep" aria-hidden="true"><i className="ti ti-building" /></span>
        <div className="rtm-sc">
          <div className="rtm-s1">Laboratory</div>
          <div className="rtm-s2">
            Depart {(stops[0]?.scheduledTime ?? '').slice(0, 5) || '—'}
          </div>
        </div>
      </div>
      {stops.length === 0 ? (
        <div className="rtm-empty">No stops on this route yet.</div>
      ) : stops.map((s, i) => {
        const st = stopStage(s);
        return (
          <div key={s.id} className="rtm-stn">
            <span className={`rtm-pin${s.completedAt ? ' done' : ''}`}>{i + 1}</span>
            <div className="rtm-sc">
              <div className="rtm-s1">
                {s.clientName ?? '—'}
                {s.serviceNo && <span className="rtm-id">{s.serviceNo}</span>}
              </div>
              <div className="rtm-s2">{s.siteAddress || '—'}</div>
              <div className="rtm-s3">
                <span className={`cr-chip ${catTone(s.categoryCode)}`}>{catName(s.categoryCode)}</span>
                <span>{(s.scheduledTime ?? '').slice(0, 5) || '—'}</span>
                <span className={`cr-chip ${st.tone}`}>{st.label}</span>
                {s.arrivedAt && <span>arrived {fmtLocalDateTime(s.arrivedAt)}</span>}
                {s.completedAt && <span>done {fmtLocalDateTime(s.completedAt)}</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * The route's own audit history, in the rail (prototype v68). Reads the ONE
 * shared audit endpoint — route edits are written under FormName "Client
 * Request" with the ROUTE as their Source — and draws it with the shared
 * timeline in the same palette as Service Tracking's (v68's colours, the
 * timeline's own layout). `version` changes after each save so it re-reads.
 */
function RouteAuditPanel({ routeId, version }: { routeId: string; version: number }) {
  const [rows, setRows] = useState<AuditTrailRow[]>([]);
  const [busy, setBusy] = useState(true);
  const [err,  setErr]  = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setBusy(true); setErr(null);
    fetchAuditTrail(AUDIT_FORM_NAME, routeId)
      .then(all => { if (alive) setRows(all); })
      // Not swallowed: a history that failed to load must not look empty.
      .catch(() => { if (alive) setErr('Could not load the audit history.'); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [routeId, version]);

  return (
    <div className="bl-card ct-rail-card">
      <h4 className="ct-rail-h">Audit</h4>
      {err ? (
        <div className="ct-audit-empty">{err}</div>
      ) : (
        <AuditTimeline rows={rows} loading={busy} palette="v68"
          emptyText="Nothing recorded yet. Save a change to this route — each altered property is written as its own audit row." />
      )}
    </div>
  );
}

/** `MM/DD/YYYY · HH:mm`, via the shared helper — never inline (§14b.6). */
function whenText(date: string | null, time: string | null): string {
  if (!date) return '';
  const t = (time ?? '').slice(0, 5);
  return t ? `${fmtLocalDate(date)} · ${t}` : fmtLocalDate(date);
}

/** How far through its stops a route is. */
function pct(r: { stopCount: number; completedCount: number }): number {
  return r.stopCount ? Math.round((r.completedCount / r.stopCount) * 100) : 0;
}

/** A stop's own stage — the same derivation `serviceStage` uses, read from the
 *  stop's stamps rather than duplicated as a stored column. */
function stopStage(r: { arrivedAt: string | null; completedAt: string | null }):
  { label: string; tone: string } {
  if (r.completedAt) return { label: 'Completed',  tone: 'ok' };
  if (r.arrivedAt)   return { label: 'On site',    tone: 'orange' };
  return                    { label: 'Dispatched', tone: 'violet' };
}

/**
 * One route: its header, its stops in visit order, and what publishing still
 * needs.
 *
 * Ordering is done here rather than by drag-and-drop deliberately: the same
 * reorder has to work on a phone in a van, and two buttons do that where a drag
 * target does not.
 */
function RouteDetail({ route, stops, techs, catName, catTone, onBack, onChanged }: {
  route:    RouteRow;
  stops:    RouteStopRow[];
  techs:    { id: string; label: string }[];
  catName:  (code?: string | null) => string;
  catTone:  (code?: string | null) => string;
  onBack:   () => void;
  onChanged: () => Promise<void>;
}) {
  const [driverId, setDriverId] = useState(route.driverId ?? '');
  /** The day being driven. Editable: a round booked for the wrong date is a
      correction the dispatcher has to be able to make, and the API and the
      procedure already accept it — only this control was read-only. */
  const [routeDate, setRouteDate] = useState((route.routeDate ?? '').slice(0, 10));
  const [vehicle,  setVehicle]  = useState(route.vehicleCode ?? '');
  const [name,     setName]     = useState(route.routeName ?? '');
  const [remark,   setRemark]   = useState(route.remark ?? '');
  /** The vehicles on offer, from the API (prototype v68). */
  const [vehicles, setVehicles] = useState<string[]>([]);
  const [busy,     setBusy]     = useState(false);
  /** Bumped after every successful change so the Audit card re-reads. */
  const [auditVer, setAuditVer] = useState(0);

  const notify = useNotify();
  const { confirm } = useConfirm();

  useEffect(() => {
    let alive = true;
    fetchRouteVehicles()
      .then(v => { if (alive) setVehicles(v); })
      // Not swallowed: without the list the dropdown still offers the route's
      // own vehicle, and the user is told why the rest are missing.
      .catch(() => { if (alive) notify.warning('Could not load the vehicle list.'); });
    return () => { alive = false; };
  }, [notify]);

  const status   = routeStatus(route);
  const blockers = routePublishBlockers({ driverId: driverId || null, vehicleCode: vehicle || null, stopCount: route.stopCount });
  const ordered  = [...stops].sort((a, b) => (a.routeSeq ?? 0) - (b.routeSeq ?? 0));
  // Once a route is running its order is locked: a driver part-way through a
  // round must not have the remaining stops rearranged under them.
  const running  = route.arrivedCount > 0;

  const save = async (publish: boolean) => {
    // Driver and date are what the field was TOLD. Changing either on a route
    // already on someone's phone is not an ordinary edit — it re-issues the
    // round — so it is confirmed in its own words rather than behind the
    // generic "save" question.
    const renotifies = !publish && !!route.publishedAt
      && (driverId !== (route.driverId ?? '') || routeDate !== (route.routeDate ?? '').slice(0, 10));

    const ok = await confirm({
      title: publish ? 'Publish route' : renotifies ? 'Change a published route?' : 'Save route',
      message: publish
        ? 'Publishing sends this route to the field and records when that happened. '
          + 'Do you want to continue?'
        : renotifies
        ? 'The driver already has this route. Changing who drives it, or the day it '
          + 'runs, re-notifies them. Do you want to continue?'
        : 'This route is saved and audited. Do you want to Save?',
    });
    if (!ok) return;

    setBusy(true);
    try {
      await updateRoute(route.id, {
        routeName: name.trim() || null,
        routeDate: routeDate || null,
        driverId:  driverId || null,
        vehicleCode: vehicle.trim() || null,
        remark: remark.trim() || null,
        publish,
      });
      notify.success(publish ? 'Published successfully.'
                   : renotifies ? 'Saved — the driver has been re-notified.'
                   : 'Saved successfully.');
      setAuditVer(v => v + 1);
      await onChanged();
    } catch (e) {
      notify.error(e instanceof Error ? `Could not save the route: ${e.message}`
                                      : 'Could not save the route.');
    } finally {
      setBusy(false);
    }
  };

  const move = async (serviceId: string, direction: 'up' | 'down' | 'remove') => {
    if (direction === 'remove') {
      const ok = await confirm({
        title: 'Remove stop',
        message: 'The service returns to the pending queue and can be put on another route. '
               + 'Do you want to continue?',
        tone: 'danger',
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      const moved = await moveRouteStop(serviceId, direction);
      if (!moved && direction !== 'remove') return;   // already first / last
      notify.success(direction === 'remove' ? 'Removed.' : 'Saved successfully.');
      setAuditVer(v => v + 1);
      await onChanged();
    } catch (e) {
      notify.error(e instanceof Error ? `Could not change the stop order: ${e.message}`
                                      : 'Could not change the stop order.');
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    const ok = await confirm({
      title: 'Delete route',
      message: 'The route is deleted and its stops return to the pending queue. '
             + 'Do you want to continue?',
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await deleteRoute(route.id);
      notify.success('Deleted.');
      await onChanged();
      onBack();
    } catch (e) {
      notify.error(e instanceof Error ? `Could not delete the route: ${e.message}`
                                      : 'Could not delete the route.');
    } finally {
      setBusy(false);
    }
  };

  // Keyboard shortcuts (CLAUDE.md standing rule 14, §18.6, ADR-0013): the route
  // detail's own actions, live only while this detail is mounted.  Ctrl+S = Save;
  // Ctrl+Enter = Publish, whose button renders only until the route is published,
  // so gate that def to match and mirror each button's `disabled`.
  useHotkeys([
    { combo: 'mod+s', label: 'Save', group: 'Client Request',
      run: () => void save(false), disabled: busy },
    { combo: 'alt+d', label: 'Delete', group: 'Client Request',
      run: () => void del(), disabled: busy || !!route.publishedAt },
    ...(!route.publishedAt
      ? [{ combo: 'mod+enter', label: 'Publish', group: 'Client Request',
           run: () => void save(true), disabled: busy || blockers.length > 0 }]
      : []),
  ]);

  return (
    <div className="bl-page mlv-host cr-detail">
      <div className="bl-grid-area ct-dpage">
        {/* v68's route detail in the project's design (owner request 2026-09-11):
            the SAME header block as Service Tracking's — back · breadcrumb, then
            the route number, its name and date, the status and Publish, the
            header's one primary action as v68 sets it. Main column in the
            order Route · Route map and stops · Stop order; the rail (Progress ·
            Rules · Audit) starts at the top beside the header. No big white
            frame: the page background shows between white cards. */}
        <div className="ct-detail">
          <div className="ct-main">
            <div className="ct-dh">
              <div className="ct-dh-crumb">
                <button type="button" className="ct-dh-back" onClick={onBack}
                        title="Back to list" aria-label="Back to Route Tracking">
                  <i className="ti ti-arrow-left" />
                </button>
                <nav className="ct-dh-bc" aria-label="Breadcrumb">
                  <button type="button" className="ct-dh-link" onClick={onBack}>
                    Route Tracking
                  </button>
                  <i className="ti ti-chevron-right" />
                  <b>{route.routeNo ?? '—'}</b>
                </nav>
              </div>
              <div className="ct-dh-row">
                <div className="ct-dh-txt">
                  <h2 className="ct-dh-t">{route.routeNo ?? '—'}</h2>
                  <div className="ct-dh-sub">
                    {[route.routeName || 'Unnamed route',
                      route.routeDate ? fmtLocalDate(route.routeDate) : null]
                      .filter(Boolean).join(' · ')}
                  </div>
                </div>
                <div className="ct-dh-acts">
                  <span className={`cr-chip ${status.tone}`}>{status.label}</span>
                  {/* Hidden once published — the pill already says so. It
                      publishes what is on screen, so a driver or vehicle picked
                      but not yet saved goes out with it; the blockers are named. */}
                  {!route.publishedAt && (
                    <button type="button" className="ca-btn ca-btn-solid"
                            disabled={busy || blockers.length > 0}
                            title={(blockers.length ? `Still needed — ${blockers.join(', ')}.` : 'Send to the field') + ` (${formatCombo('mod+enter')})`}
                            onClick={() => void save(true)}>
                      <span className="tb-i"><i className="ti ti-send" /></span> Publish
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="bl-card ct-rec">
              <h3 className="ct-rec-h">Route</h3>
              {/* Two EQUAL columns (v68): what publishing needs first —
                  Driver | Vehicle — then Route date | Published. v68 has no
                  Route name field; ours keeps one, across the full width,
                  because an unnamed route has nowhere else to be named.

                  The label sits BESIDE its control in a fixed-width column over
                  a 50/50 split (owner request 2026-09-28, "refer the layout ...
                  from Settings → Inventory Management → Items"). That layout is
                  the SHARED `sd-grid--side` from SettingsDetailPage.css — the
                  same class ModuleCrudConfig.sideLabels switches on — so this
                  card now uses the standard rather than the private
                  `rt-fgrid` / `rt-f` grid it had, which was a second
                  implementation of the same thing (rule 8) and has been
                  deleted. `rt-side` carries only this card's label width. */}
              <div className="sd-grid sd-grid--side rt-side">
                <div className="sd-row">
                  <label className="sd-label" htmlFor="rt-d-driver">Driver</label>
                  <select id="rt-d-driver" className="sd-input" value={driverId}
                          onChange={e => setDriverId(e.target.value)}>
                    <option value="">— unassigned —</option>
                    {techs.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                </div>
                <div className="sd-row">
                  <label className="sd-label" htmlFor="rt-d-vehicle">Vehicle</label>
                  {/* A dropdown of the vehicles on offer (prototype v68), served by
                      the API. A route's own vehicle is always offered even if it
                      is no longer on the list, so saving never silently drops it. */}
                  <select id="rt-d-vehicle" className="sd-input" value={vehicle}
                          onChange={e => setVehicle(e.target.value)}>
                    <option value="">— select —</option>
                    {vehicles.map(v => <option key={v} value={v}>{v}</option>)}
                    {!!vehicle && !vehicles.includes(vehicle) && (
                      <option value={vehicle}>{vehicle}</option>
                    )}
                  </select>
                </div>
                <div className="sd-row">
                  <label className="sd-label" htmlFor="rt-d-date">Route date</label>
                  <input id="rt-d-date" className="sd-input" type="date" value={routeDate}
                         onChange={e => setRouteDate(e.target.value)} />
                </div>
                <div className="sd-row">
                  <label className="sd-label">Published</label>
                  <input className="sd-input readonly" readOnly disabled
                         value={route.publishedAt ? fmtLocalDateTime(route.publishedAt) : 'Not published'} />
                </div>
                {/* Route name | Remark share the third line (owner request
                    2026-09-28) rather than taking a full-width row each, so the
                    card is three even pairs and the Save row comes up to meet
                    them. */}
                <div className="sd-row">
                  <label className="sd-label" htmlFor="rt-d-name">Route name</label>
                  <input id="rt-d-name" className="sd-input" value={name} maxLength={120}
                         placeholder="Optional — e.g. Portland Metro — Daily"
                         onChange={e => setName(e.target.value)} />
                </div>
                <div className="sd-row">
                  {/* The dispatcher's note on the run — shown in the Stops
                      list's Remark column (v68), saved and audited with the rest. */}
                  <label className="sd-label" htmlFor="rt-d-remark">Remark</label>
                  <textarea id="rt-d-remark" className="sd-input" rows={2} maxLength={500}
                            value={remark} placeholder="Optional — a note for this run"
                            onChange={e => setRemark(e.target.value)} />
                </div>
              </div>

              <div className="rt-acts">
                <button className="ca-btn" disabled={busy}
                        title={`Save (${formatCombo('mod+s')})`}
                        onClick={() => void save(false)}>
                  <span className="tb-i"><i className="ti ti-device-floppy" /></span> Save
                </button>
                <span className="sa-bulk-gap" />
                <button className="ca-btn cr-discard" disabled={busy || !!route.publishedAt}
                        title={(route.publishedAt
                          ? 'A published route has gone to the field and cannot be deleted'
                          : 'Delete this route') + ` (${formatCombo('alt+d')})`}
                        onClick={() => void del()}>
                  <span className="tb-i"><i className="ti ti-trash" /></span> Delete
                </button>
              </div>
            </div>

            {/* The map (prototype v68). It sits ABOVE the stop order rather than
                replacing it: the table is the authority — it is what dispatch
                edits and what survives a geocoder that cannot answer — and the
                map is the picture of it. */}
            <div className="bl-card ct-rec">
              <h3 className="ct-rec-h">Route map and stops</h3>
              <div className="rt-maprow">
                <RouteMap stops={ordered} />
                <RouteStations stops={ordered} catName={catName} catTone={catTone} />
              </div>
            </div>

            <div className="bl-card ct-rec">
              <h3 className="ct-rec-h">Stop order</h3>
              {ordered.length === 0 ? (
                <div className="rt-empty">No stops on this route.</div>
              ) : (
                <table className="rt-stops">
                  <thead>
                    <tr>
                      <th>Seq</th><th>Service</th><th>Category</th><th>Client</th>
                      <th>Where</th><th>Time</th><th>Stage</th><th>Order</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ordered.map((s, i) => {
                      const st = stopStage(s);
                      return (
                        <tr key={s.id}>
                          <td><span className="rt-seq">{s.routeSeq ?? i + 1}</span></td>
                          <td>{s.serviceNo ?? '—'}</td>
                          <td><span className={`cr-chip ${catTone(s.categoryCode)}`}>
                            {catName(s.categoryCode)}</span></td>
                          <td>{s.clientName ?? '—'}</td>
                          <td className="rt-dim">{s.siteAddress ?? '—'}</td>
                          <td>{(s.scheduledTime ?? '').slice(0, 5) || '—'}</td>
                          <td><span className={`cr-chip ${st.tone}`}>{st.label}</span></td>
                          <td>
                            <div className="rt-order">
                              <button className="rt-ob" disabled={busy || running || i === 0}
                                      title={running ? 'The route is running — order is locked' : 'Move up'}
                                      onClick={() => void move(s.id, 'up')}>
                                <i className="ti ti-arrow-up" />
                              </button>
                              <button className="rt-ob"
                                      disabled={busy || running || i === ordered.length - 1}
                                      title={running ? 'The route is running — order is locked' : 'Move down'}
                                      onClick={() => void move(s.id, 'down')}>
                                <i className="ti ti-arrow-down" />
                              </button>
                              <button className="rt-ob danger" disabled={busy || running}
                                      title={running ? 'The route is running — stops are locked' : 'Remove from route'}
                                      onClick={() => void move(s.id, 'remove')}>
                                <i className="ti ti-x" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <aside className="ct-rail">
            <div className="bl-card ct-rail-card">
              <h4 className="ct-rail-h">Progress</h4>
              <span className="rt-bar big">
                <span className="rt-bar-fill" style={{ width: `${pct(route)}%` }} />
              </span>
              <div className="rt-progress-t">
                {route.completedCount} of {route.stopCount} stop
                {route.stopCount === 1 ? '' : 's'} complete
              </div>
            </div>

            <div className="bl-card ct-rail-card">
              <h4 className="ct-rail-h">Rules</h4>
              <ul className="rt-rules">
                <li><b>Status</b><span>Derived from the stops</span></li>
                <li><b>Publish needs</b><span>Driver + vehicle + a stop</span></li>
                <li><b>Once running</b><span>Order locked</span></li>
                <li><b>Published</b><span>Cannot be deleted</span></li>
              </ul>
            </div>

            <RouteAuditPanel routeId={route.id} version={auditVer} />
          </aside>
        </div>
      </div>
    </div>
  );
}
